// The phone's way to a new announcement: the legacy MobileSelection wizard.
//
// First the kind of stand-in — with one, without, the office — then the kind
// of announcement among those the company has for that choice; the drawer
// opens with the type already chosen. Only the buttons the company's types
// allow are shown, as before.

import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Grid, IconButton, Typography, useTheme } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import type { FormDescriptor } from "~/api";
import { useBootstrap } from "~/bootstrap";
import { useT } from "~/i18n";
import { modeOf } from "~/components/clock/clock";
import type { Mode } from "~/components/clock/dates";
import CalendarIcon from "~/legacy/icons/CalendarIcon";
import CalendarNoEndIcon from "~/legacy/icons/CalendarNoEndIcon";
import DateTimeIcon from "~/legacy/icons/DateTimeIcon";
import ForwardIcon from "~/legacy/icons/ForwardIcon";
import OfficeIcon from "~/legacy/icons/OfficeIcon";
import PersonIcon from "~/legacy/icons/PersonIcon";
import PersonOutlinedIcon from "~/legacy/icons/PersonOutlinedIcon";
import TimeIcon from "~/legacy/icons/TimeIcon";
import { DashboardDrawer, type DrawerRequest } from "./drawer";

type Kind = "person" | "none" | "office";

const kindOf = (m: FormDescriptor): Kind =>
  m.proxy?.source === "secretariats" ? "office" : m.proxy?.source === "users" ? "person" : "none";

const label = { fontSize: "14px", fontWeight: 400, lineHeight: "20px", overflow: "hidden" } as const;

function Choice({
  show,
  first,
  icon,
  text,
  onClick,
}: {
  show: boolean;
  first?: boolean;
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
}) {
  return (
    <Button
      variant="contained"
      color="secondary"
      fullWidth
      onClick={onClick}
      startIcon={icon}
      endIcon={<ForwardIcon fontSize={32} />}
      disableElevation
      sx={{ mt: first ? 0 : 1, borderRadius: "10px", textTransform: "none", height: "64px", display: show ? "flex" : "none" }}
    >
      <Typography noWrap sx={label}>
        {text}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
    </Button>
  );
}

export function PickPage() {
  const theme = useTheme();
  const t = useT();
  const b = useBootstrap();
  const navigate = useNavigate();
  const location = useLocation();
  const selectionType = new URLSearchParams(location.search).get("type") || "announcement";

  const [kind, setKind] = useState<Kind | null>(null);
  const [request, setRequest] = useState<DrawerRequest | null>(null);

  useEffect(() => {
    document.title = t("vmm-select");
    document.body.style.backgroundColor = theme.palette.background.default;
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, [t, theme.palette.background.default]);

  const plannable = useMemo(() => b.messages.filter((m) => m.canPlan), [b.messages]);
  const has = (k: Kind) => plannable.some((m) => kindOf(m) === k);
  const forKind = (mode: Mode) => plannable.find((m) => kindOf(m) === kind && modeOf(m) === mode);

  const choose = (mode: Mode) => {
    const m = forKind(mode);
    if (!m) return;
    setRequest({ kind: "new", messageId: m.companyMessageId, favourite: selectionType === "favourite" });
  };

  return (
    <Fragment>
      <Typography
        sx={{
          ml: 2,
          mt: -3,
          fontSize: "30px",
          fontWeight: 700,
          lineHeight: "34px",
          letterSpacing: "-0.75px",
          position: "relative",
          zIndex: theme.zIndex.appBar + 101,
        }}
      >
        {selectionType === "favourite" ? t("new-favorite") : t("new-voicemail")}
      </Typography>
      {kind ? (
        <Fragment>
          <Grid container sx={{ pl: 1, mt: 1.5 }}>
            <IconButton onClick={() => setKind(null)}>
              <ArrowBackIosNewIcon sx={{ color: "primary.main" }} />
            </IconButton>
            <Typography sx={{ fontSize: "20px", fontWeight: 600, lineHeight: "28px", letterSpacing: "-0.3px", mt: 0.5, ml: 0.5 }}>
              {t("choose-announcement")}
            </Typography>
          </Grid>
          <Box sx={{ px: 2, mt: 2 }}>
            <Choice first show={!!forKind("time")} icon={<TimeIcon />} text={t("time-announcement")} onClick={() => choose("time")} />
            <Choice show={!!forKind("date")} icon={<CalendarIcon />} text={t("date-announcement")} onClick={() => choose("date")} />
            <Choice
              show={!!forKind("datetime")}
              icon={<DateTimeIcon />}
              text={t("datetime-announcement")}
              onClick={() => choose("datetime")}
            />
            <Choice show={!!forKind("open")} icon={<CalendarNoEndIcon />} text={t("without-enddate")} onClick={() => choose("open")} />
          </Box>
        </Fragment>
      ) : (
        <Fragment>
          <Grid container sx={{ pl: 1, mt: 1.5 }}>
            <IconButton onClick={() => navigate("/")}>
              <ArrowBackIosNewIcon sx={{ color: "primary.main" }} />
            </IconButton>
            <Typography sx={{ fontSize: "20px", fontWeight: 600, lineHeight: "28px", letterSpacing: "-0.3px", mt: 0.5, ml: 0.5 }}>
              {t("choose-representation")}
            </Typography>
          </Grid>
          <Box sx={{ px: 2, mt: 2 }}>
            <Choice first show={has("person")} icon={<PersonIcon />} text={t("with-representation")} onClick={() => setKind("person")} />
            <Choice show={has("none")} icon={<PersonOutlinedIcon />} text={t("without-representation")} onClick={() => setKind("none")} />
            <Choice show={has("office")} icon={<OfficeIcon />} text={t("office-representation")} onClick={() => setKind("office")} />
          </Box>
          {plannable.length === 0 ? (
            <Typography color="text.secondary" sx={{ px: 2, mt: 2 }}>
              {t("no-announcement-types")}
            </Typography>
          ) : null}
        </Fragment>
      )}
      <DashboardDrawer request={request} onClose={() => setRequest(null)} />
    </Fragment>
  );
}
